

import React, { FC, useEffect, useMemo } from "react";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import Select from "@/components/Select/Select";
import { useAppTheme } from "@/utils/theme";
import { useUserStore } from "@/hooks/useUserStore";
import { useGetUserPrograms } from "@/hooks/usePrograms";
import { SkeletonClientFilters } from "@/ui/menus/clients/Skeletons";
import { ParticipantStatus } from "@/domain/participant.enum";

export type Filters = {
  sortField: string;
  sortOrder: number;
  pageNumber: number;
  pageSize: number;
  search: string;
  status: string[];
  programId: string;
  orgUnitId: string;
};

type Props = {
  filtersState: [Filters, React.Dispatch<React.SetStateAction<Filters>>];
};

const ALL_PROGRAMS = { key: "All", value: "All Programs" };
const ClientFilters: FC<Props> = ({ filtersState }) => {
  const theme = useAppTheme();
  const [filters, setFilters] = filtersState;
  const { userInfo, hasSystemAdminRole } = useUserStore();

  const { programs, isLoadingPrograms, isFetchingPrograms } =
    useGetUserPrograms({
      userId: userInfo?._id as string,
      sortType: "desc",
      hasSystemAdminRole,
    });

  const programsOptions = useMemo(() => {
    if (!programs) return [];
    const result = programs.map((program) => ({
      key: program._id,
      value: program.name,
    }));

    /* if (result?.length > 1) {
      result.sort((a, b) => a.value.localeCompare(b.value));
      return [ALL_PROGRAMS, ...result];
    }
    return result */
    result.sort((a, b) => a.value.localeCompare(b.value));
    return [ALL_PROGRAMS, ...result];
  }, [programs]);

  const organizationUnitsOptions = useMemo(() => {
    if (!programs || !filters.programId) return [];

    const selectedProgram = programs.find((p) => p._id === filters.programId);
    if (!selectedProgram || !selectedProgram.orgUnitIds) return [];
    return selectedProgram.orgUnitIds.map((orgUnit) => ({
      key: orgUnit._id,
      value: orgUnit.name,
    }));
  }, [programs, filters.programId]);

  useEffect(() => {
    if (programs && programs.length > 0 && !filters.programId) {
      /* if (programs.length > 1) {
        setFilters((prevFilters) => ({
          ...prevFilters,
          programId: "All",
          sortField: "fullName",
          sortOrder: 1,
        }));
        return;
      }

      setFilters((prevFilters) => ({
        ...prevFilters,
        programId: programs[0]._id,
      })); */

      setFilters((prevFilters) => ({
        ...prevFilters,
        programId: "All",
        sortField: "fullName",
        sortOrder: 1,
      }));
    }
  }, [programs, filters.programId, setFilters]);

  useEffect(() => {
    // When programId changes, reset orgUnitId and then auto-select first one
    if (
      filters.programId &&
      filters.programId !== "All" &&
      organizationUnitsOptions.length > 0
    ) {
      setFilters((prevFilters) => ({
        ...prevFilters,
        orgUnitId: organizationUnitsOptions[0].key,
        pageNumber: 0,
      }));
    }

    if (filters.programId === "All") {
      setFilters((prevFilters) => ({
        ...prevFilters,
        orgUnitId: "",
        pageNumber: 0,
        sortOrder: 1,
      }));
    }
  }, [filters.programId, organizationUnitsOptions, setFilters]);

  return (
    <Box
      sx={{
        display: "flex",
        gap: "1.25rem",
        width: "100%",
        flexDirection: {
          xs: "column",
          sm: "column",
          md: "column",
        },
      }}
    >
      {isFetchingPrograms || isLoadingPrograms ? (
        <SkeletonClientFilters />
      ) : (
        <>
          <Box
            sx={{
              backgroundColor: theme.palette.surface.light2,
              px: 2,
              width: {
                xs: "100%",
                sm: "100%",
              },
            }}
            className="flex rounded-lg py-3 max-w-full items-center justify-between h-14"
          >
            <InputBase
              placeholder="Search participant"
              inputProps={{ "aria-label": "search" }}
              onChange={(e) => {
                setFilters((filters) => ({
                  ...filters,
                  search: e.target.value,
                  pageNumber: 0,
                }));
              }}
              value={filters.search}
            />
            <SearchIcon className="text-icon-primary" />
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: "0.5rem",
              width: {
                xs: "100%",
                sm: "100%",
              },
              flexDirection: {
                xs: "column",
                sm: "row",
                md: "row",
              },
            }}
          >
            <Select
              handleChange={(value) => {
                setFilters((filters) => ({
                  ...filters,
                  programId: value as string,
                  pageNumber: 0,
                  sortField: value === "All" ? "fullName" : filters.sortField,
                  sortOrder: 1,
                }));
              }}
              data={programsOptions || []}
              value={filters.programId}
              label="Program"
              id="users-program"
              isLoading={isLoadingPrograms}
              maxWidth={320}
              fullWidth
            />

            <Select
              handleChange={(value) => {
                setFilters((filters) => ({
                  ...filters,
                  orgUnitId: value as string,
                  pageNumber: 0,
                }));
              }}
              data={organizationUnitsOptions || []}
              value={filters.orgUnitId}
              label="Organization Unit"
              id="organization-unit"
              isLoading={isLoadingPrograms}
              maxWidth={320}
              disabled={filters.programId === "All"}
              fullWidth
            />

            <Select
              handleChange={(value) => {
                setFilters((filters) => ({
                  ...filters,
                  status: value as string[],
                  pageNumber: 0,
                }));
              }}
              data={[
                { key: ParticipantStatus.ACTIVE, value: "Active" },
                { key: ParticipantStatus.PAUSED, value: "Paused" },
                { key: ParticipantStatus.COMPLETED, value: "Completed" },
                { key: ParticipantStatus.DISENROLLED, value: "Dismissed" },
                { key: ParticipantStatus.CANCELED, value: "Canceled" },
              ]}
              value={filters.status[0] === "" ? [] : filters.status}
              label="Status"
              id="participant-status"
              multiple
              maxWidth={320}
              fullWidth
            />
          </Box>
        </>
      )}
    </Box>
  );
};

export default ClientFilters;

